import React from 'react';
import { ImageElement, ImageValue, MediaType, StaticOrDynamic, zImageStaticSource } from '../../../core/template.schemas';
import { useTemplateStore } from '../../hooks/useTemplateStore';
import { Label } from '@/ui-components/ui/label';
import { Input } from '@/ui-components/ui/input';
import { Image as ImageIcon } from 'lucide-react';
import { CommonProperties } from './common';
import { AdvancedStaticDynamicSelector, elementAttributeRegistry } from '../../registry';

interface ImagePropertiesProps {
  element: ImageElement;
  onOpenSlotPanel?: () => void;
}

/**
 * Image Properties Component
 * Displays image-specific properties like source, fit and clip radius
 */
export const ImageProperties: React.FC<ImagePropertiesProps> = ({ element, onOpenSlotPanel }) => {
  const updateElement = useTemplateStore(s => s.updateElement);

  // Get attribute mappings for image elements
  const imageMapping = elementAttributeRegistry.get('image');
  const imageSourceAttribute = imageMapping?.attributes.find(attr => attr.attributeKey === 'source');

  const handlePropertyChange = (property: keyof ImageElement, value: any) => {
    updateElement(element.id, { [property]: value });
  };

  const handleSourceChange = (
    sourceValue: StaticOrDynamic<any, MediaType> | null,
    meta?: { width?: number; height?: number }
  ) => {
    // If a new image is selected, calculate the new height based on the aspect ratio
    if (sourceValue?.mode === 'static' && meta?.width && meta.height) {
      const newHeight = (element.width / meta.width) * meta.height;
      updateElement(element.id, { source: sourceValue as ImageValue, height: newHeight });
      return;
    }

    if (sourceValue) {
      // When clearing a static image, `staticValue` can become `null`.
      // The schema requires an object, so we ensure a valid empty object is passed.
      if (sourceValue.mode === 'static' && !sourceValue.staticValue) {
        handlePropertyChange('source', {
          ...sourceValue,
          staticValue: zImageStaticSource.parse({}),
        } as ImageValue);
      } else {
        handlePropertyChange('source', sourceValue as ImageValue);
      }
    } else {
      // This handles cases where the entire source object is cleared.
      handlePropertyChange('source', {
        mode: 'static',
        mediaType: 'image',
        staticValue: zImageStaticSource.parse({}),
      });
    }
  };

  const handleClipRadiusChange = (radius: number) => {
    handlePropertyChange('clipRadius', radius);
  };

  return (
    <div className="space-y-4">
      <CommonProperties element={element} />
      
      {/* Image-specific Properties */}
      <div className="space-y-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-2">
          <ImageIcon className="w-3.5 h-3.5 text-gray-500" />
          <Label className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">Bild</Label>
        </div>
        
        {/* Image Source - Using AdvancedStaticDynamicSelector */}
        {imageSourceAttribute && (
          <div className="bg-gray-50/30 p-2 rounded-xl border border-gray-100">
            <AdvancedStaticDynamicSelector
              label="Bildquelle"
              value={element.source}
              onChange={handleSourceChange}
              compatibleMediaTypes={imageSourceAttribute.compatibleMediaTypes}
              required={imageSourceAttribute.required}
              onOpenSlotPanel={onOpenSlotPanel}
              allowedMimeTypes={['image/jpeg', 'image/png', 'image/gif', 'image/webp', 'image/heic', 'image/heif']}
            />
          </div>
        )}

        {/* Border Radius (ClipRadius) - Static Input */}
        <div className="space-y-1.5">
          <Label className="text-[11px] text-gray-500 font-medium">Eckenradius</Label>
          <div className="relative">
            <Input
              type="number"
              value={element.clipRadius || 0}
              onChange={(e) => handleClipRadiusChange(parseFloat(e.target.value) || 0)}
              className="h-8 text-[11px] rounded-lg border-gray-200 pr-7"
              min="0"
              step="1"
            />
            <span className="text-[10px] text-gray-400 absolute right-2 top-2 font-medium">px</span>
          </div>
        </div>
      </div>
    </div>
  );
};